import { ConnectionStatus } from '@yandex-video-platform/goloom-sdk';
import { eventChannel } from 'redux-saga';
import { all, call, delay, put, race, select, spawn, take, takeLeading } from 'typed-redux-saga';

import { RootState } from '../../../../../init-store';
import { goloomSdk } from '../../../../shared/features/goloom-sdk';
import {
    hideNotification,
    NOTIFICATIONS_MAP,
    notify,
} from '../../../../shared/features/notifications/ps-notifications';
import { countMetrika } from '../../../../shared/helpers/metrika';
import offlineMonitor from '../../../../shared/helpers/offline';
import { takeLatestThrottled } from '../../../../shared/helpers/redux-saga';
import { NetworkState } from '../../constants';
import { getNetworkState, getRoomId } from '../../selectors';
import { ConnectionStatusEvent, NotificationId } from '../../types';
import { setOnlineInChat } from '../chat';
import { reconnectConference } from '../conference/slice';
import { unshareScreenAction } from '../screen-sharing/slice';
import { logger, waitingRoomUpdateSaga } from '../waiting-rooms';
import { networkStateOffline, networkStateOnline, setNetworkState } from './slice';

enum OFFLINE_EVENTS {
    OFFLINE = 'offline',
    ONLINE = 'online',
    RECONNECTING = 'reconnecting',
    DISCONNECTED = 'disconnected',
}

/**
 * @param {NetworkState} networkState
 */
function* notifyNetworkState(networkState: NetworkState) {
    const roomId = yield* select(getRoomId);

    switch (networkState) {
        case NetworkState.OFFLINE:
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.OFFLINE]()));
            yield* call(countMetrika, 'no_connection_message_shown', roomId);
            break;

        case NetworkState.RECONNECTING:
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.RECONNECTING]()));
            yield* call(countMetrika, 'reconnect_in_progress_message_shown', roomId);
            break;

        case NetworkState.ONLINE:
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.ONLINE]()));
            yield* call(countMetrika, 'reconnected_message_shown', roomId);
            break;
    }
}

/**
 * @param {NetworkState} networkState
 */
function* deferNotifyNetworkState(networkState: NetworkState) {
    const { timeout } = yield* race({
        timeout: delay(5000),
        online: take(networkStateOnline),
    });

    if (timeout) {
        yield* call(notifyNetworkState, networkState);
    }
}

export function* setOnline() {
    yield* put(networkStateOnline());

    const currentNetworkState = yield* select(getNetworkState);

    if (currentNetworkState === NetworkState.RECONNECTING) {
        yield* put(hideNotification(NotificationId.RECONNECTING));
        yield* call(notifyNetworkState, NetworkState.ONLINE);
        yield* put(setNetworkState(NetworkState.ONLINE));
    } else if (currentNetworkState === NetworkState.OFFLINE) {
        yield* call(notifyNetworkState, NetworkState.ONLINE);
        yield* put(setNetworkState(NetworkState.ONLINE));
    }
    yield* call(setOnlineInChat);
}

function createOfflineChannel() {
    return eventChannel<OFFLINE_EVENTS>((emitter) => {
        const handleOffline = () => {
            emitter(OFFLINE_EVENTS.OFFLINE);
        };
        const handleOnline = () => {
            emitter(OFFLINE_EVENTS.ONLINE);
        };
        const handleConnectionStatusChanged = ({ current: status }: ConnectionStatusEvent) => {
            if (status === ConnectionStatus.RECONNECTING) {
                emitter(OFFLINE_EVENTS.RECONNECTING);
            } else if (status === ConnectionStatus.CONNECTED) {
                emitter(OFFLINE_EVENTS.ONLINE);
            } else if (status === ConnectionStatus.DISCONNECTED) {
                // Когда мы закрываем крышку, RTC соединение закрывается.
                // @see https://a.yandex-team.ru/arcadia/videoplatform/webrtc/goloom/sdk/browser/lib/src/service/peer-connection/peer-connection.controller.ts?rev=r15635514#L368
                // Мы эмитим DISCONNECTED, попытаться реконектнуться.
                emitter(OFFLINE_EVENTS.DISCONNECTED);
            }
        };

        offlineMonitor.addOfflineEventListener(handleOffline);
        offlineMonitor.addOnlineEventListener(handleOnline);
        goloomSdk.onConnectionStatusChanged.addListener(handleConnectionStatusChanged);

        return () => {
            offlineMonitor.removeOfflineEventListener(handleOffline);
            offlineMonitor.removeOnlineEventListener(handleOnline);
            goloomSdk.onConnectionStatusChanged.removeListener(handleConnectionStatusChanged);
        };
    });
}

/**
 * Обработчик ухода в офлайн
 *
 */
function* offlineSaga(event: OFFLINE_EVENTS) {
    switch (event) {
        case OFFLINE_EVENTS.OFFLINE: {
            yield put(setNetworkState(NetworkState.OFFLINE));
            yield put(networkStateOffline());
            yield spawn(deferNotifyNetworkState, NetworkState.OFFLINE);
            yield put(unshareScreenAction());
            break;
        }
        case OFFLINE_EVENTS.ONLINE: {
            yield call(setOnline);
            break;
        }
        case OFFLINE_EVENTS.RECONNECTING: {
            yield put(setNetworkState(NetworkState.RECONNECTING));
            yield call(notifyNetworkState, NetworkState.RECONNECTING);
            break;
        }
        case OFFLINE_EVENTS.DISCONNECTED: {
            yield call(notifyNetworkState, NetworkState.RECONNECTING);
            yield put(setNetworkState(NetworkState.RECONNECTING));
            yield put(reconnectConference({ reason: 'ConferenceStatus was DISCONNECTED' }));
        }
    }
}

export function* watchOfflineSaga() {
    const offlineChannel = createOfflineChannel();

    yield takeLeading(offlineChannel, offlineSaga);
}

export function* watchOnlineSaga() {
    yield* takeLatestThrottled(3000, networkStateOffline, function* () {
        const stateSnapshot = yield* select();
        yield* take(networkStateOnline);
        const restoringSagas: Array<(state: RootState) => unknown> = [waitingRoomUpdateSaga];
        try {
            yield* all(restoringSagas.map((saga) => call(saga, stateSnapshot)));
        } catch (error) {
            logger.error(error, {
                method: 'watchOnlineSaga',
            });
        }
    });
}
